<template>
  <section class="reservas">
    <div class="d-flex flex-wrap align-center justify-space-between mb-4" style="gap: 12px">
      <div class="d-flex flex-wrap align-center" style="gap: 8px" role="group" aria-label="Filtrar por estado">
        <button
          v-for="option in statusFilterOptions"
          :key="option.id"
          type="button"
          class="rs-filter"
          :class="{ 'rs-filter--active': statusFilter === option.id }"
          :aria-pressed="statusFilter === option.id ? 'true' : 'false'"
          @click="statusFilter = option.id"
        >
          <span v-if="option.tone" class="rs-dot" :class="`rs-dot--${option.tone}`"></span>
          {{ option.name }}
          <span class="rs-filter__count">{{ statusCounts[option.id] || 0 }}</span>
        </button>
        <v-checkbox
          v-model="showApprovalFlow"
          class="ml-2 mt-0 pt-0"
          label="Incluir en aprobación"
          hide-details
          dense
        ></v-checkbox>
      </div>
      <div class="d-flex flex-wrap" style="gap: 8px">
        <v-btn outlined color="grey darken-2" :loading="loading" @click="refresh">
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
        <v-btn
          v-if="showAdminActions"
          color="primary"
          depressed
          :loading="loading"
          @click="handleRunPromotionEngine"
        >
          Revisar stock y enviar las listas
        </v-btn>
      </div>
    </div>

    <!-- Purchase received but stock still short: nothing will unblock these on its own -->
    <section v-if="blockedByStockRows.length" role="alert" class="rs-alert mb-4">
      <div class="d-flex align-start" style="gap: 12px">
        <span class="rs-alert__icon" aria-hidden="true">
          <v-icon small color="#B42318">mdi-alert-outline</v-icon>
        </span>
        <div>
          <div class="rs-alert__title">
            {{
              blockedByStockRows.length === 1
                ? "1 solicitud bloqueada por falta de stock"
                : `${blockedByStockRows.length} solicitudes bloqueadas por falta de stock`
            }}
          </div>
          <div class="rs-muted">
            Su compra ya llegó, pero el stock con que contaba se usó en otra parte. No se van a
            destrabar solas.
          </div>
        </div>
      </div>
      <div
        v-for="row in blockedByStockRows"
        :key="row.id"
        class="rs-alert__row d-flex flex-wrap align-center justify-space-between"
      >
        <span>
          <span class="rs-mono font-weight-medium">{{ getSliCode(row) }}</span>
          · {{ row.place_id }} · falta
          <strong>{{ missingMaterials(row).map((m) => `${m.code} × ${m.missing}`).join(", ") }}</strong>
        </span>
        <v-btn
          v-if="showAdminActions"
          small
          depressed
          color="primary"
          :loading="creatingPurchaseFor === row.id"
          @click="createStockPurchase(row)"
        >
          Generar compra
        </v-btn>
      </div>
    </section>

    <v-data-table
      class="rs-table"
      :headers="headers"
      :items="filteredRows"
      :items-per-page="20"
      :loading="loading"
      loading-text="Cargando reservas"
      item-key="id"
      show-expand
      :expanded.sync="expanded"
      :item-class="rowClass"
      no-data-text="No hay reservas del Cubicador con este filtro"
      @item-expanded="onItemExpanded"
    >
      <template v-slot:item.fifo_position="{ item }">
        <span v-if="item.queueEntry" class="font-weight-medium">#{{ item.queueEntry.fifo_position }}</span>
        <span v-else class="rs-muted">-</span>
      </template>
      <template v-slot:item.id="{ item }">
        <div class="rs-mono">{{ getSliCode(item) }}</div>
        <div class="rs-caption">
          {{ shortUser(item.created_by) }} · {{ formatDate(item.created_at) }}
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <span class="rs-chip" :class="`rs-chip--${item.displayStatus.tone}`">
          <span class="rs-dot" :class="`rs-dot--${item.displayStatus.tone}`"></span>
          {{ item.displayStatus.label }}
        </span>
        <span
          v-if="item.queueEntry && item.queueEntry.is_urgent"
          class="rs-chip rs-chip--urgent ml-1"
          :title="item.queueEntry.urgent_comment || ''"
        >
          Urgente
        </span>
      </template>
      <template v-slot:item.total_amount="{ item }">
        {{
          item.total_amount
            ? "$ " + item.total_amount.toLocaleString("es", optionsAmountFormatting)
            : "-"
        }}
      </template>
      <template v-slot:item.sap_code="{ item }">
        <span class="rs-mono">{{ item.sap_code || "-" }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-end align-center" style="gap: 2px">
          <v-btn
            v-if="showAdminActions && item.queueEntry && item.queueEntry.blocked_by_stock"
            text
            small
            color="primary"
            :loading="creatingPurchaseFor === item.id"
            @click="createStockPurchase(item)"
          >
            Generar compra
          </v-btn>
          <!-- Anyone who sees the reservation can mark it urgent (business decision, 2026-10-14) -->
          <v-btn v-if="item.queueEntry" text small color="grey darken-3" @click="toggleUrgent(item)">
            {{ item.queueEntry.is_urgent ? "Quitar urgencia" : "Urgente" }}
          </v-btn>
          <v-menu v-if="showAdminActions && item.queueEntry" offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" aria-label="Más acciones" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                :disabled="!item.queueEntry.shortfall_fulfilled"
                @click="openForcePromoteDialog(item)"
              >
                <v-list-item-title>Forzar envío</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="rs-detail">
          <div v-if="item.queueEntry" class="d-flex flex-wrap rs-detail__body">
            <div class="rs-detail__steps">
              <div class="rs-overline">Para salir a picking</div>
              <div v-for="step in checklist(item)" :key="step.key" class="rs-step">
                <span class="rs-step__icon" :class="`rs-step__icon--${step.state}`" aria-hidden="true">
                  <v-icon x-small :color="stepIconColor[step.state]">{{ stepIcon[step.state] }}</v-icon>
                </span>
                <div>
                  <div class="rs-step__title" :class="`rs-step__title--${step.state}`">{{ step.title }}</div>
                  <div v-for="(line, index) in step.lines" :key="index" class="rs-muted">{{ line }}</div>
                </div>
              </div>
            </div>
            <div class="rs-detail__materials">
              <div class="rs-overline">Materiales</div>
              <div class="rs-box">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th class="text-right">Requerido</th>
                        <th class="text-right">Stock en su turno</th>
                        <th class="text-right">Falta</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="material in item.queueEntry.materials"
                        :key="material.code"
                        :class="{ 'rs-row-short': material.missing > 0 }"
                      >
                        <td class="rs-mono">{{ material.code }}</td>
                        <td>{{ materialDescription(item, material.code) }}</td>
                        <td class="text-right">{{ material.quantity }}</td>
                        <td
                          class="text-right"
                          :class="{ 'rs-short': material.stock_available < material.quantity }"
                        >
                          {{ material.stock_available }}
                        </td>
                        <td class="text-right" :class="{ 'rs-short': material.missing > 0 }">
                          {{ material.missing > 0 ? material.missing : "-" }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
              <div v-if="showAdminActions" class="d-flex flex-wrap justify-end mt-3" style="gap: 8px">
                <v-btn
                  outlined
                  small
                  color="grey darken-2"
                  :disabled="!item.queueEntry.shortfall_fulfilled"
                  @click="openForcePromoteDialog(item)"
                >
                  Forzar envío
                </v-btn>
                <v-btn
                  v-if="item.queueEntry.blocked_by_stock"
                  small
                  depressed
                  color="primary"
                  :loading="creatingPurchaseFor === item.id"
                  @click="createStockPurchase(item)"
                >
                  Generar compra por lo que falta
                </v-btn>
              </div>
            </div>
          </div>
          <div v-else-if="item.displayStatus.id === 'promoted'" class="rs-detail__body">
            <div class="rs-overline">Trasladada a SAP</div>
            <div v-if="detailFor(item).loading" class="rs-muted">Cargando…</div>
            <template v-else>
              <div v-if="promotedAt(item)" class="rs-muted mb-2">
                Salió a picking el {{ formatDate(promotedAt(item)) }}.
              </div>
              <div class="rs-box" style="max-width: 720px">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr><th>Código</th><th>Descripción</th><th class="text-right">Cantidad</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="line in transferItems(item)" :key="line.code">
                        <td class="rs-mono">{{ line.code }}</td>
                        <td>{{ line.description || "-" }}</td>
                        <td class="text-right">{{ line.quantity }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </template>
          </div>
          <div v-else class="rs-detail__body rs-muted">
            Esta solicitud aún no llega a SAP: {{ item.displayStatus.label.toLowerCase() }}.
          </div>
        </td>
      </template>
    </v-data-table>

    <v-dialog v-if="showAdminActions" v-model="dialogForcePromote" max-width="500px">
      <v-card>
        <v-card-title class="text-h6">
          Forzar envío de la solicitud {{ selectedBundle && getSliCode(selectedBundle) }}
        </v-card-title>
        <v-card-text>
          <p>
            Esta acción envía la reserva a SAP como traslado real,
            saltándose el orden FIFO/excedente. Debe quedar registrada con el
            centro de costo del empleado que la solicitó.
          </p>
          <v-text-field
            label="Forzado por"
            v-model="forcePromoteForm.override_by"
            outlined
            dense
            readonly
            hint="Tu propio correo de sesión - no editable, para que el registro sea confiable"
            persistent-hint
          ></v-text-field>
          <v-text-field
            class="mt-4"
            label="Centro de costo del solicitante"
            :value="
              loadingEmployeeCostCenter
                ? 'Buscando...'
                : forcePromoteForm.override_cost_center || 'No encontrado'
            "
            outlined
            dense
            readonly
            hint="Obtenido automáticamente desde la ficha del solicitante"
            persistent-hint
          ></v-text-field>
          <v-textarea
            class="mt-4"
            label="Motivo"
            v-model="forcePromoteForm.override_comment"
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" outlined @click="dialogForcePromote = false">
            <small>Cancelar</small>
          </v-btn>
          <v-btn
            color="red darken-2"
            class="text-white"
            :loading="loadingForcePromote"
            :disabled="loadingEmployeeCostCenter || !forcePromoteForm.override_cost_center"
            @click="confirmForcePromote"
          >
            <small>Forzar envío</small>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { authUsuarioComputed } from "@/state/helpers";
import { OPTIONS_AMOUNT_FORMATTING } from "@/constants/common";
import config from "@/config";
import { getSliCode, formatPickingDayCl } from "@/helpers/cubage";

// Composite status: ms-cubicador's own bundle.status only knows about
// pending_approval/rejected/posted_to_sap - it has no idea whether a posted bundle has
// since cleared the FIFO/stock queue in ms-entrega-materiales. Blending the two into one
// status answers "what's going on with this request" in one chip. Tones: red = needs an
// action, orange = waiting on a purchase, blue = ready, green = done, grey = not in SAP yet.
function getDisplayStatus(bundle, queueEntry) {
  if (bundle.status === "pending_approval") {
    return { id: "pending_approval", label: "En aprobación", tone: "grey" };
  }
  if (bundle.status === "rejected") {
    return { id: "rejected", label: "Rechazada", tone: "grey" };
  }
  if (bundle.status === "superseded") {
    return { id: "superseded", label: "Reemplazada", tone: "grey" };
  }
  if (bundle.status === "cancelled") {
    return { id: "cancelled", label: "Anulada", tone: "grey" };
  }
  if (!queueEntry) {
    return { id: "promoted", label: "Trasladada", tone: "green" };
  }
  if (queueEntry.blocked_by_stock) {
    const short = queueEntry.materials.filter((material) => material.missing > 0);
    return {
      id: "no_stock",
      label: short.length === 1 ? `Sin stock · falta ${short[0].missing}` : `Sin stock · ${short.length} materiales`,
      tone: "red",
    };
  }
  if (!queueEntry.shortfall_fulfilled) {
    return { id: "waiting_purchase", label: "Esperando compra", tone: "orange" };
  }
  if (queueEntry.waiting_picking_window) {
    return { id: "ready", label: `Lista · picking desde ${formatPickingDayCl(queueEntry.picking_from)}`, tone: "blue" };
  }
  if (queueEntry.ready_to_promote) {
    return { id: "ready", label: "Lista · sale en la próxima revisión", tone: "blue" };
  }
  return { id: "queued", label: "En cola", tone: "grey" };
}

// By default only bundles that already reached SAP show - the approval-flow ones are noise
// for the "what's happening in SAP" question this view answers, until the checkbox asks.
const POST_SAP_STATUS_OPTIONS = [
  { id: "all", name: "Todas" },
  { id: "no_stock", name: "Sin stock", tone: "red" },
  { id: "waiting_purchase", name: "Esperando compra", tone: "orange" },
  { id: "ready", name: "Lista", tone: "blue" },
  { id: "promoted", name: "Trasladada", tone: "green" },
];
const APPROVAL_FLOW_STATUS_OPTIONS = [
  { id: "pending_approval", name: "En aprobación", tone: "grey" },
  { id: "rejected", name: "Rechazada", tone: "grey" },
  { id: "superseded", name: "Reemplazada", tone: "grey" },
  { id: "cancelled", name: "Anulada", tone: "grey" },
];
const APPROVAL_FLOW_IDS = APPROVAL_FLOW_STATUS_OPTIONS.map((option) => option.id);

export default {
  props: {
    // "mine" fetches only the current user's own bundles (get_bundles_by_user).
    // "all" fetches every bundle from every requester (get_all_bundles, admin use).
    mode: {
      type: String,
      default: "mine",
      validator: (value) => ["mine", "all"].includes(value),
    },
    // Admin-only affordances: force-promote, "run promotion engine now" and "Generar
    // compra". Regular requesters see status and detail but can't trigger a real SAP
    // posting or bypass the FIFO/surplus gate.
    showAdminActions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bundles: [],
      loading: false,
      statusFilter: "all",
      showApprovalFlow: false,
      optionsAmountFormatting: OPTIONS_AMOUNT_FORMATTING,
      dialogForcePromote: false,
      loadingForcePromote: false,
      loadingEmployeeCostCenter: false,
      selectedBundle: null,
      creatingPurchaseFor: null,
      expanded: [],
      // entrega's bundle_status per bundle id, loaded when its row is opened
      details: {},
      forcePromoteForm: {
        override_by: "",
        override_cost_center: "",
        override_comment: "",
      },
      stepIcon: { ok: "mdi-check", wait: "mdi-clock-outline", blocked: "mdi-close" },
      stepIconColor: { ok: "#1B5E20", wait: "#9A5200", blocked: "#B42318" },
      headers: [
        { text: "", value: "data-table-expand", width: "40px" },
        { text: "Pos.", align: "start", sortable: false, value: "fifo_position", width: "64px" },
        { text: "Solicitud", align: "start", sortable: false, value: "id" },
        { text: "Proyecto", align: "start", sortable: false, value: "place_id" },
        { text: "Estado", align: "start", sortable: false, value: "status" },
        { text: "Monto", align: "end", sortable: false, value: "total_amount" },
        { text: "ST SAP", align: "start", sortable: false, value: "sap_code" },
        { text: "Acciones", align: "end", sortable: false, value: "actions" },
      ],
    };
  },
  mounted() {
    this.refresh();
  },
  computed: {
    ...authUsuarioComputed,
    ...mapGetters({
      pendingBundles: "promotion/pendingBundles",
    }),
    statusFilterOptions() {
      return this.showApprovalFlow
        ? [...POST_SAP_STATUS_OPTIONS, ...APPROVAL_FLOW_STATUS_OPTIONS]
        : POST_SAP_STATUS_OPTIONS;
    },
    rows() {
      const rows = this.bundles.map((bundle) => {
        const queueEntry = this.pendingBundles.find(
          (entry) => entry.cubicador_bundle_id === String(bundle.id)
        );
        return {
          ...bundle,
          sap_code: bundle.sap_code || (queueEntry && queueEntry.sap_code) || null,
          queueEntry,
          displayStatus: getDisplayStatus(bundle, queueEntry),
        };
      });
      // The queue first, in FIFO order; then everything else, newest first
      return rows.sort((a, b) => {
        if (a.queueEntry && b.queueEntry) return a.queueEntry.fifo_position - b.queueEntry.fifo_position;
        if (a.queueEntry || b.queueEntry) return a.queueEntry ? -1 : 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    },
    visibleRows() {
      return this.showApprovalFlow
        ? this.rows
        : this.rows.filter((row) => !APPROVAL_FLOW_IDS.includes(row.displayStatus.id));
    },
    statusCounts() {
      const counts = { all: this.visibleRows.length };
      this.visibleRows.forEach((row) => {
        counts[row.displayStatus.id] = (counts[row.displayStatus.id] || 0) + 1;
      });
      return counts;
    },
    filteredRows() {
      if (this.statusFilter === "all") return this.visibleRows;
      return this.visibleRows.filter((row) => row.displayStatus.id === this.statusFilter);
    },
    // Purchase received but stock still short: nothing will unblock these on its own
    blockedByStockRows() {
      return this.visibleRows.filter((row) => row.queueEntry && row.queueEntry.blocked_by_stock);
    },
  },
  watch: {
    showApprovalFlow(nowShowing) {
      if (!nowShowing && APPROVAL_FLOW_IDS.includes(this.statusFilter)) {
        this.statusFilter = "all";
      }
    },
  },
  methods: {
    getSliCode,
    formatPickingDayCl,
    ...mapActions({
      getBundlesByUser: "cubage/getBundlesByUser",
      getAllBundles: "cubage/getAllBundles",
      getPendingBundles: "promotion/getPendingBundles",
      getBundleStatus: "promotion/getBundleStatus",
      runPromotionEngine: "promotion/runPromotionEngine",
      forcePromoteBundle: "promotion/forcePromoteBundle",
      setBundleUrgent: "promotion/setBundleUrgent",
      getEmployees: "maintainers/getEmployees",
      createBundleStockPurchase: "cubage/createBundleStockPurchase",
    }),
    async refresh() {
      this.loading = true;
      const [bundlesResp] = await Promise.all([
        this.mode === "mine"
          ? this.getBundlesByUser({ user: this.user.email })
          : this.getAllBundles(),
        this.getPendingBundles(),
      ]);
      this.bundles = Array.isArray(bundlesResp) ? bundlesResp : [];
      // Open rows keep their detail fresh too
      this.details = {};
      this.expanded.forEach((item) => this.loadDetail(item));
      this.loading = false;
    },
    rowClass(item) {
      return item.displayStatus.id === "no_stock" ? "rs-row-blocked" : "";
    },
    shortUser(email) {
      return email ? email.split("@")[0] : "-";
    },
    formatDate(value) {
      return value
        ? new Date(value).toLocaleDateString("es-CL", { timeZone: "America/Santiago" })
        : "-";
    },
    onItemExpanded({ item, value }) {
      if (value) this.loadDetail(item);
    },
    // entrega's bundle_status (DB only): purchases with their POs/receipts and the
    // transfer's lines with descriptions, for the checklist and the material detail
    async loadDetail(item) {
      if (this.details[item.id] && !this.details[item.id].error) return;
      this.$set(this.details, item.id, { loading: true });
      const data = await this.getBundleStatus({ bundle_id: item.id });
      this.$set(this.details, item.id, { loading: false, data, error: !data });
    },
    detailFor(item) {
      return this.details[item.id] || { loading: true };
    },
    transferItems(item) {
      const detail = this.detailFor(item);
      const transfer = detail.data && detail.data.transfers && detail.data.transfers[0];
      return transfer ? transfer.items : [];
    },
    promotedAt(item) {
      const detail = this.detailFor(item);
      const transfer = detail.data && detail.data.transfers && detail.data.transfers[0];
      return transfer ? transfer.promoted_at : null;
    },
    materialDescription(item, code) {
      const line = this.transferItems(item).find((entry) => entry.code === code);
      if (line && line.description) return line.description;
      return this.detailFor(item).loading ? "…" : "-";
    },
    missingMaterials(item) {
      return item.queueEntry.materials.filter((material) => material.missing > 0);
    },
    // The three things a queued request needs to go to picking, each ok / wait / blocked
    checklist(item) {
      const entry = item.queueEntry;
      const detail = this.detailFor(item);
      const purchaseLines = [];
      ((detail.data && detail.data.purchases) || []).forEach((purchase) => {
        purchase.items
          .filter((line) => !line.cancelled)
          .forEach((line) => {
            const order = line.purchase_orders.length ? `OC ${line.purchase_orders.join(", ")}` : "sin OC";
            const eta = line.delivery_date ? ` · llegada estimada ${this.formatDate(line.delivery_date)}` : "";
            purchaseLines.push(
              `SC ${purchase.sap_code} · ${line.code}: recibido ${line.received_quantity || 0} de ${line.quantity} · ${order}${eta}`
            );
          });
      });
      let purchase;
      if (detail.loading) {
        purchase = { key: "purchase", state: entry.shortfall_fulfilled ? "ok" : "wait", title: "Compra", lines: ["Cargando…"] };
      } else if (!purchaseLines.length) {
        purchase = { key: "purchase", state: "ok", title: "Compra", lines: ["No necesitó compra: había stock al enviarse."] };
      } else if (entry.shortfall_fulfilled) {
        purchase = { key: "purchase", state: "ok", title: "Compra recibida", lines: purchaseLines };
      } else {
        purchase = { key: "purchase", state: "wait", title: "Esperando compra", lines: purchaseLines };
      }

      const short = this.missingMaterials(item);
      const shortText = short.map((material) => `${material.code} × ${material.missing}`).join(", ");
      let stock;
      if (!short.length) {
        stock = { key: "stock", state: "ok", title: "Stock reservado en su turno", lines: [] };
      } else if (entry.shortfall_fulfilled) {
        stock = {
          key: "stock",
          state: "blocked",
          title: `Stock: falta ${shortText}`,
          lines: ["El stock con que contaba se usó en otra parte (una ST no reserva stock en SAP)."],
        };
      } else {
        stock = {
          key: "stock",
          state: "wait",
          title: `Stock: falta ${shortText}`,
          lines: ["Debería cubrirlo la compra al recibirse."],
        };
      }

      let picking;
      if (entry.is_urgent) {
        picking = { key: "picking", state: "ok", title: "Urgente", lines: ["No espera la ventana de picking."] };
      } else if (!entry.picking_from) {
        picking = { key: "picking", state: "ok", title: "Sin fecha de despacho", lines: ["Sale apenas esté lista."] };
      } else if (new Date(entry.picking_from) <= new Date()) {
        picking = {
          key: "picking",
          state: "ok",
          title: "Ventana de picking abierta",
          lines: [`Desde el ${formatPickingDayCl(entry.picking_from)}.`],
        };
      } else {
        picking = {
          key: "picking",
          state: "wait",
          title: `Picking desde el ${formatPickingDayCl(entry.picking_from)}`,
          lines: ["2 días hábiles antes de la fecha de despacho."],
        };
      }
      return [purchase, stock, picking];
    },
    async handleRunPromotionEngine() {
      const resp = await this.runPromotionEngine();
      if (resp && resp.status === 200) {
        this.$swal({
          icon: "success",
          title: "Excelente",
          text: "Stock revisado y reservas listas enviadas a SAP",
          position: "top-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
        });
        this.refresh();
      } else {
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Ha ocurrido un error al actualizar la cola, intenta nuevamente",
          position: "top-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    },
    // Moves a pending reservation ahead of the non-urgent ones in the FIFO queue (or back).
    // It still waits for its purchase shortfall and for stock, it only jumps the line.
    async toggleUrgent(item) {
      const urgent = !item.queueEntry.is_urgent;
      const result = await this.$swal({
        icon: "question",
        title: urgent
          ? `¿Marcar ${getSliCode(item)} como urgente?`
          : `¿Quitar la urgencia de ${getSliCode(item)}?`,
        text: urgent
          ? "Pasará antes que las reservas no urgentes en la cola FIFO."
          : "Volverá a su lugar por fecha en la cola FIFO.",
        input: "textarea",
        inputPlaceholder: "Motivo",
        inputValidator: (value) => (urgent && !value ? "Indica el motivo" : undefined),
        showCancelButton: true,
        confirmButtonText: "Confirmar",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      this.loading = true;
      const resp = await this.setBundleUrgent({
        cubicador_bundle_id: String(item.id),
        urgent,
        user: this.user ? this.user.email : "",
        comment: result.value || null,
      });
      this.loading = false;
      const ok = resp && resp.status === 200;
      this.$swal({
        icon: ok ? "success" : "error",
        title: ok ? "Excelente" : "Error",
        text: ok
          ? "Cola FIFO actualizada"
          : (resp && resp.response && resp.response.data && resp.response.data.detail) ||
            "Ha ocurrido un error al actualizar la urgencia",
        position: "top-end",
        showConfirmButton: false,
        toast: true,
        timer: 3000,
        timerProgressBar: true,
      });
    },
    // Purchase request in SAP for what the request is missing; it then waits for that
    // purchase to be received instead of for stock nobody is buying
    async createStockPurchase(item) {
      const lines = this.missingMaterials(item)
        .map((material) => `<li><b>${material.code}</b>: ${material.missing}</li>`)
        .join("");
      const result = await this.$swal({
        icon: "question",
        title: `¿Generar solicitud de compra para ${getSliCode(item)}?`,
        html: `<p>Se creará en SAP una solicitud de compra por lo que falta:</p><ul class="text-left">${lines}</ul>
          <p>La solicitud quedará esperando que esa compra se reciba.</p>`,
        input: "textarea",
        inputPlaceholder: "Comentario (opcional)",
        showCancelButton: true,
        confirmButtonText: "Generar compra",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      this.creatingPurchaseFor = item.id;
      const resp = await this.createBundleStockPurchase({
        bundle_id: item.id,
        user: this.user ? this.user.email : "",
        comment: result.value || null,
      });
      this.creatingPurchaseFor = null;
      if (resp.error) {
        this.$swal({ icon: "error", title: "No se pudo generar la compra", text: resp.error });
        return;
      }
      this.$swal({
        icon: "success",
        title: "Excelente",
        text: resp.message,
        position: "top-end",
        showConfirmButton: false,
        toast: true,
        timer: 3000,
        timerProgressBar: true,
      });
      this.refresh();
    },
    async openForcePromoteDialog(item) {
      this.selectedBundle = item;
      // override_by is locked to the acting admin's own session email (never editable -
      // a free-text field here would let anyone attribute the override to someone else)
      // and override_cost_center is looked up from the requester's own employee record
      // (never typed either, for the same reason: it must be the real cost center on
      // file, not whatever an admin types).
      this.forcePromoteForm = {
        override_by: this.user ? this.user.email : "",
        override_cost_center: "",
        override_comment: "",
      };
      this.dialogForcePromote = true;
      this.loadingEmployeeCostCenter = true;
      const resp = await this.getEmployees({
        password: config.password_manteiners,
        user: item.created_by,
      });
      this.loadingEmployeeCostCenter = false;
      const employee =
        resp && resp.employees && resp.employees.length ? resp.employees[0] : null;
      this.forcePromoteForm.override_cost_center =
        (employee && employee.cost_center && employee.cost_center.code) || "";
    },
    async confirmForcePromote() {
      if (
        !this.forcePromoteForm.override_by ||
        !this.forcePromoteForm.override_cost_center ||
        !this.forcePromoteForm.override_comment
      ) {
        return this.$swal({
          icon: "error",
          title: "Error",
          text:
            "Falta el centro de costo del solicitante o el motivo - no se puede forzar sin esa información",
          position: "top-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      this.loadingForcePromote = true;
      const resp = await this.forcePromoteBundle({
        cubicador_bundle_id: String(this.selectedBundle.id),
        override_by: this.forcePromoteForm.override_by,
        override_comment: this.forcePromoteForm.override_comment,
        override_cost_center: this.forcePromoteForm.override_cost_center,
      });
      this.loadingForcePromote = false;
      if (resp && resp.status === 200) {
        this.dialogForcePromote = false;
        this.$swal({
          icon: "success",
          title: "Excelente",
          text: "Reserva enviada a SAP correctamente",
          position: "top-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
        });
        this.refresh();
      } else {
        this.$swal({
          icon: "error",
          title: "Error",
          text:
            (resp && resp.data && resp.data.detail) ||
            "Ha ocurrido un error al forzar el envío",
          position: "top-end",
          showConfirmButton: false,
          toast: true,
          timer: 4000,
          timerProgressBar: true,
        });
      }
    },
  },
};
</script>

<style scoped>
.reservas {
  color: #1f2937;
}
.rs-muted {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.45;
}
.rs-caption {
  font-size: 12px;
  color: #6b7280;
}
.rs-mono {
  font-family: "Roboto Mono", ui-monospace, monospace;
  font-size: 13px;
}
.rs-overline {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 10px;
}

/* Filter chips */
.rs-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #d5dce4;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
}
.rs-filter--active {
  background: #e8f1fc;
  border-color: #b8d3f3;
  color: #0d47a1;
}
.rs-filter__count {
  font-size: 12px;
  opacity: 0.75;
}

/* Status chips and dots: one tone per meaning */
.rs-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.rs-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}
.rs-chip--red { background: #fdecec; color: #9b1c1c; }
.rs-chip--orange { background: #fff4e5; color: #7a3e00; }
.rs-chip--blue { background: #e8f1fc; color: #0d47a1; }
.rs-chip--green { background: #e7f5ec; color: #1b5e20; }
.rs-chip--grey { background: #eef1f4; color: #374151; }
.rs-chip--urgent { background: #fdecec; color: #9b1c1c; border: 1px solid #f3c7c7; }
.rs-dot--red { background: #d93025; }
.rs-dot--orange { background: #e08600; }
.rs-dot--blue { background: #1976d2; }
.rs-dot--green { background: #2e7d32; }
.rs-dot--grey { background: #9aa4b2; }

/* Alert */
.rs-alert {
  background: #ffffff;
  border: 1px solid #f3c7c7;
  border-radius: 10px;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rs-alert__icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fdecec;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.rs-alert__title {
  font-size: 15px;
  font-weight: 500;
}
.rs-alert__row {
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fbf4f4;
  font-size: 14px;
}

/* Table */
.rs-table {
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  overflow: hidden;
}
.rs-table ::v-deep th {
  font-weight: 500 !important;
  color: #4b5563 !important;
  background: #fafbfc;
}
.rs-table ::v-deep .rs-row-blocked {
  background: #fffbfb;
}
.rs-detail {
  background: #fafbfc;
  padding: 0 !important;
}
.rs-detail__body {
  gap: 24px;
  padding: 18px 24px 22px 56px;
}
.rs-detail__steps {
  flex: 1 1 320px;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rs-detail__materials {
  flex: 2 1 480px;
  min-width: 300px;
}
.rs-box {
  background: #ffffff;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  overflow-x: auto;
}
.rs-step {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.rs-step__icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.rs-step__icon--ok { background: #e7f5ec; }
.rs-step__icon--wait { background: #fff4e5; }
.rs-step__icon--blocked { background: #fdecec; }
.rs-step__title {
  font-size: 14px;
  font-weight: 500;
}
.rs-step__title--blocked { color: #9b1c1c; }
.rs-step__title--wait { color: #7a3e00; }
.rs-row-short { background: #fffbfb; }
.rs-short {
  color: #9b1c1c;
  font-weight: 500;
}
</style>
